import React, { JSX, useCallback, useEffect, useState } from 'react';
import {
    getConversationalSearchContentClass,
    SearchFilterContentTypes,
    SearchOption,
    SearchOrigins,
} from 'search-gateway-client/components';
import { useStatsigExperimentContext } from 'statsig/components';
import { twMerge } from 'tailwind-merge';

import { NavSearchContext, NavSearchContextProvider, NavSearchProps } from '../context/NavSearchContext';
import { useSearchWithAI } from '../hooks/useSearchWithAI';
import { CrossAssetNavSuggestions } from '../types/shared';
import getConversationalSearchUrl from '../utils/getConversationalSearchUrl';
import { useMedia } from '../utils/useMedia';
import { saveSearchHistory } from './AutosuggestElements/utils';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import { cleanSearchKeywords } from './NavSearchElements/searchUtils';
import RightArrowButton from './ProminentSearchElements/RightArrowButton';
import SearchWithAIToggle from './ProminentSearchElements/SearchWithAIToggle';
import ProminentSearchLegacyButton from './ProminentSearchLegacyElements/ProminentSearchLegacyButton';
import ProminentSearchDropdown from './ProminentSearchLegacyElements/ProminentSearchLegacyDropdown';
import SearchWithAudio from './SearchWithAudioButton';

export interface ProminentSearchProps extends NavSearchProps {
    onDropdownChange: (contentType: SearchFilterContentTypes) => void;
    searchTerm: string;
    prominentSearchOptions: SearchOption[];
    contentTypeFromPrevious: SearchFilterContentTypes;
    showDropDown: boolean;
    showContentToggles?: boolean;
    useNewSearchUI?: boolean;
    alignment?: 'left' | 'center';
    showConversationalSearchCta?: boolean;
}

//homepage hero yellow underline for all content types
const activeUnderline =
    'homepage-hero-product-active underline border-0 decoration-[0.2rem] underline-offset-5 decoration-yellow';
const inactiveUnderline = 'homepage-hero-product text-gray-300 border-0';

export default function ProminentSearchLegacy(props: ProminentSearchProps): JSX.Element {
    const {
        onDropdownChange,
        onSubmit,
        userHasAnySubscription,
        prominentSearchOptions,
        contentTypeFromPrevious,
        showDropDown,
        showContentToggles = false,
        useNewSearchUI = false,
        useSnapshots = false,
        alignment = 'left',
        showConversationalSearchCta = false,
    } = props;

    const defaultSearchOption = prominentSearchOptions?.[0]?.id ?? contentTypeFromPrevious;

    const [contentType, setContentType] = useState<SearchFilterContentTypes>(
        defaultSearchOption ?? contentTypeFromPrevious,
    );
    const [searchTerm, setSearchTerm] = useState(props.searchTerm);
    const { isSearchHistoryEnabled } = useStatsigExperimentContext();
    const { conversationalSearchEnabled, getPlaceholder, isSearchWithAIEnabledForContentType } = useSearchWithAI({
        showConversationalSearchCta,
        contentType,
    });

    const aiCtaEnabledForContentType = React.useMemo(
        () => isSearchWithAIEnabledForContentType(contentType),
        [contentType, isSearchWithAIEnabledForContentType],
    );

    // Ensure searchTerm stays up to date when given a new one via props.
    useEffect(() => {
        setSearchTerm(props.searchTerm);
    }, [props.searchTerm]);

    useEffect(() => {
        setContentType(contentTypeFromPrevious);
    }, [contentTypeFromPrevious]);

    // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
    const isMediumScreen = useMedia('(min-width: 672px)', true);

    const handleDropdownChange = (contentType: string) => {
        onDropdownChange(contentType as SearchFilterContentTypes);
        setContentType(contentType as SearchFilterContentTypes);
    };

    const makeEmptySuggestions = (): CrossAssetNavSuggestions => ({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
    });

    const [suggestions, setSuggestions] = useState(makeEmptySuggestions());

    const clearSuggestions = useCallback(() => setSuggestions(makeEmptySuggestions()), []);

    const handleSubmit = useCallback(
        (
            contentTypeSearched: SearchFilterContentTypes = contentType,
            value: string = searchTerm,
            searchOriginOverride?: SearchOrigins,
        ) => {
            const cleanedSearchKeywords = cleanSearchKeywords(value);
            const searchOrigin = searchOriginOverride ?? SearchOrigins.SEARCH_BAR;

            if (isSearchHistoryEnabled) {
                saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
            }

            if (conversationalSearchEnabled) {
                window.location.href = getConversationalSearchUrl(
                    cleanedSearchKeywords,
                    searchOrigin,
                    getConversationalSearchContentClass(contentTypeSearched),
                );
                return;
            }

            onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
        },
        [contentType, searchTerm, conversationalSearchEnabled, isSearchHistoryEnabled, onSubmit],
    );

    return (
        <NavSearchContextProvider {...props}>
            <NavSearchContext.Consumer>
                {({ setShowSuggestions }) => (
                    <div
                        className={twMerge(
                            'flex flex-col w-full font-inter',
                            useNewSearchUI ? 'rounded-full border border-solid border-gray-400' : 'text-gray-900',
                            showContentToggles ? 'pb-5' : '',
                            alignment === 'center' && 'items-center',
                        )}
                    >
                        {showContentToggles && (
                            <div className="product m-0 flex text-white text-base font-bold grid-rows-4 pb-5 cursor-pointer">
                                {prominentSearchOptions.map((link) => (
                                    // Contents of the product div
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        key={link.id}
                                        className={twMerge(
                                            'text-sm pr-2 mobile-md:text-base ppro-sm:pr-4 ppro-sm:text-base md:pr-6 md:text-base lg:pr-6 lg:text-base',
                                            contentType === link.id ? activeUnderline : inactiveUnderline,
                                        )}
                                        data-value={link.id}
                                        onClick={() => handleDropdownChange(link.id)}
                                        onKeyDown={() => handleDropdownChange(link.id)}
                                    >
                                        {link.label}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex items-center justify-center gap-4">
                            <form
                                className={twMerge(
                                    'border-none rounded-full lg:w-[720px] md:w-[500px] flex items-center bg-white',
                                    !useNewSearchUI && 'pl-5',
                                    showConversationalSearchCta && 'pl-0',
                                )}
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    setShowSuggestions(false);

                                    handleSubmit(contentType, searchTerm);
                                }}
                            >
                                {showDropDown && (
                                    <>
                                        <ProminentSearchDropdown
                                            onChange={handleDropdownChange}
                                            value={contentType}
                                            useNewSearchUI={useNewSearchUI}
                                            useSnapshots={useSnapshots}
                                            clearSuggestions={clearSuggestions}
                                        />
                                        {!useNewSearchUI && (
                                            <div>
                                                <div className="w-px h-11 bg-gray-500" />
                                            </div>
                                        )}
                                    </>
                                )}
                                {showConversationalSearchCta && (
                                    <SearchWithAIToggle disabledForContentType={!aiCtaEnabledForContentType} />
                                )}
                                {/* {only serve CrossAssetNavSearchBox experience for new searchUI (search pages), otherwise continue to use legacy experiences} */}
                                {useNewSearchUI ? (
                                    <CrossAssetNavSearchBox
                                        contentType={contentType}
                                        setSearchTerm={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        useNewSearchUI
                                        placeholder={getPlaceholder(
                                            prominentSearchOptions.find(({ id }) => id === contentType)
                                                ?.placeholderText,
                                            contentType,
                                        )}
                                        suggestions={suggestions}
                                        setSuggestions={setSuggestions}
                                        clearSuggestions={clearSuggestions}
                                    />
                                ) : isMediumScreen ? (
                                    <CrossAssetNavSearchBox
                                        contentType={contentType}
                                        setSearchTerm={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        suggestions={suggestions}
                                        setSuggestions={setSuggestions}
                                        clearSuggestions={clearSuggestions}
                                        placeholder={getPlaceholder(
                                            prominentSearchOptions.find(({ id }) => id === contentType)
                                                ?.placeholderText,
                                            contentType,
                                        )}
                                    />
                                ) : (
                                    <NavSearchBox
                                        contentType={contentType}
                                        onChange={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        placeholder={getPlaceholder(
                                            prominentSearchOptions.find(({ id }) => id === contentType)
                                                ?.placeholderText,
                                            contentType,
                                        )}
                                        userHasAnySubscription={userHasAnySubscription}
                                        isProminentSearch
                                    />
                                )}
                                {showConversationalSearchCta ? (
                                    <RightArrowButton />
                                ) : (
                                    <ProminentSearchLegacyButton
                                        primary={showContentToggles}
                                        useNewSearchUI={useNewSearchUI}
                                    />
                                )}
                            </form>
                            <SearchWithAudio contentType={contentType} />
                        </div>
                    </div>
                )}
            </NavSearchContext.Consumer>
        </NavSearchContextProvider>
    );
}
